import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Wrench, AlertCircle, Copy, Check, Eye, Pencil } from "lucide-react";
import { getClient, listTools } from "../../../api/chatbotApi";
import useAuth from "../../../hooks/useAuth";
import { isPlatformAdmin, isTenantAdmin } from "../../../util/accessControl";

export default function ChatbotToolsPage() {
  const { clientId } = useParams();
  const clientToolsPath = `/clients/${clientId}/tools`;
  const [tools, setTools] = useState([]);
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedToolId, setCopiedToolId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditTools = isPlatformAdmin(user) || isTenantAdmin(user);

  useEffect(() => {
    if (!clientId) return;
    getClient(clientId)
      .then((client) => setClientName(client?.name || "Client"))
      .catch(() => setClientName("Client"));
  }, [clientId]);

  useEffect(() => {
    setLoading(true);
    setError("");
    listTools(clientId)
      .then((response) => {
        const items = [response, response?.items, response?.tools, response?.data, response?.data?.items]
          .find(Array.isArray) || [];
        setTools(items);
      })
      .catch((err) => setError(err.message || "Failed to load tools"))
      .finally(() => setLoading(false));
  }, [clientId]);

  const typeBadgeClass = (type) => {
    switch (type) {
      case "python":
        return "bg-blue-100 text-blue-700";
      case "sql":
        return "bg-emerald-100 text-emerald-700";
      case "http":
        return "bg-amber-100 text-amber-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const handleCopyToolId = async (toolId) => {
    await navigator.clipboard.writeText(String(toolId));
    setCopiedToolId(toolId);
    setTimeout(() => setCopiedToolId(null), 1500);
  };

  return (
    <div className="space-y-6">
      {clientId ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/clients" className="inline-flex items-center gap-1 transition hover:text-slate-900">
            <ArrowLeft size={16} /> Clients
          </Link>
          <span>/</span>
          <Link to={`/clients/${clientId}`} className="transition hover:text-slate-900">
            {clientName || "Client"}
          </Link>
          <span>/</span>
          <span className="font-semibold text-slate-900">Tools</span>
        </div>
      ) : null}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{clientName || "Client"} AI Tools</h1>
          <p className="text-sm text-slate-500 mt-1">
            Tools available to this client — Python, SQL, and HTTP REST executors
          </p>
        </div>
        {canEditTools ? (
          <button
            onClick={() => navigate(`${clientToolsPath}/new`)}
            className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0959bb] transition"
          >
            <Plus size={18} /> New Tool
          </button>
        ) : null}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-slate-500 text-sm">
            Loading tools...
          </div>
        ) : tools.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 mb-3">
              <Wrench size={24} />
            </div>
            <h3 className="text-base font-semibold text-slate-900">No tools registered</h3>
            <p className="text-sm text-slate-500 mt-1">No tools have been registered for this client yet.</p>
            {canEditTools ? (
              <button
                onClick={() => navigate(`${clientToolsPath}/new`)}
                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                <Plus size={16} /> Create First Tool
              </button>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {tools.map((t) => (
              <article key={t.id} className="flex min-h-64 flex-col rounded-2xl border border-slate-200 bg-slate-50/50 p-5 transition hover:-translate-y-0.5 hover:border-[#096dea]/30 hover:bg-white hover:shadow-md">
                <div className="-mx-5 -mt-5 flex items-start justify-between gap-3 rounded-t-2xl border-b border-[#d7e9ff] bg-[#eef5ff] p-5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate text-base font-bold text-slate-900" title={t.name}>{t.name}</h2>
                      <button
                        type="button"
                        onClick={() => handleCopyToolId(t.id)}
                        className="shrink-0 text-slate-400 transition hover:text-[#096dea]"
                        aria-label={`Copy ID for ${t.name}`}
                        title={copiedToolId === t.id ? "Tool ID copied" : "Copy tool ID"}
                      >
                        {copiedToolId === t.id ? <Check size={15} /> : <Copy size={15} />}
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-mono font-semibold ${typeBadgeClass(t.executor_type)}`}>
                        {t.executor_type}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${t.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                        {t.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                  {canEditTools ? (
                    <button
                      type="button"
                      onClick={() => navigate(`${clientToolsPath}/${t.id}`)}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-[#eef5ff] hover:text-[#096dea]"
                      aria-label={`Edit ${t.name}`}
                      title="Edit tool"
                    >
                      <Pencil size={17} />
                    </button>
                  ) : null}
                </div>
                <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-500">
                  {t.description || "No description provided."}
                </p>

                <div className="mt-auto flex gap-2 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(`${clientToolsPath}/${t.id}?mode=view`)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]"
                  >
                    <Eye size={16} /> View
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
